import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Table } from '@interfaces/others';
import { ModalController } from '@ionic/angular';
import { SipService } from '@services/sip.service';
import { Subscription } from 'rxjs';
import { Journey, ColorType } from '@interfaces/rest/interfaces/journey';

@Component({
  selector: 'app-services-modal',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>Servicios de {{ displayName }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()">
            <ion-icon slot="icon-only" name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <app-table 
        [columns]="columns" 
        [lines]="journeys" 
        (doActionParent)="handleAction($event)">
      </app-table>
    </ion-content>
  `
})
export class ServicesModalComponent implements OnInit, OnDestroy {
  @Input() columns?: Table[];
  @Input() journeys?: any[];
  @Input() displayName?: string;
  @Input() callerId?: string;
  @Input() onAction?: (ev: any) => Promise<void>;

  private subscription?: Subscription;

  constructor(
    private modalController: ModalController,
    private sip: SipService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    // 🔥 Suscribirse a cambios en los datos del usuario para actualizar el listado
    if (this.callerId) {
      // Cargar datos iniciales desde el servicio
      this.updateJourneys();

      // Suscribirse a futuras actualizaciones
      this.subscription = this.sip.userDataUpdate$.subscribe(() => {
        this.updateJourneys();
        this.cdr.detectChanges(); // Forzar detección de cambios
      });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * 🔥 Actualiza los journeys desde el servicio
   */
  private updateJourneys() {
    if (!this.callerId) return;

    const userData = this.sip.getUserData(this.callerId);
    if (userData?.journeys) {
      this.journeys = this.transformJourneysForTable(userData.journeys);
    } else {
      this.journeys = [];
    }
  }

  /**
   * 🔥 Verifica si un journey está en estado activo (permite editar/duplicar/cancelar)
   */
  private isJourneyActive(journey: Journey): boolean {
    return journey.status === 'NEW' ||
      journey.status === 'DRIVER_SEARCHING' ||
      journey.status === 'DRIVER_FOUND' ||
      journey.status === 'DRIVER_CONFIRMED' ||
      journey.status === 'DRIVER_ARRIVED' ||
      journey.status === 'TRIP_STARTED';
  }

  /**
   * 🔥 Transforma los journeys para la tabla
   */
  private transformJourneysForTable(journeys: Journey[]): any[] {
    return journeys.map(journey => {
      const color = this.getStatusColor(journey.status);

      return {
        ...journey,
        from: journey.fromAddress
          ? `${journey.fromAddress.title || ''} - ${journey.fromAddress.description || ''}`.trim()
          : '',
        to: journey.toAddress
          ? `${journey.toAddress.title || ''} - ${journey.toAddress.description || ''}`.trim()
          : '',
        statusObj: journey.status ? [{
          name: journey.status,
          translatePath: 'services',
          color: color
        }] : [],
        // 🔥 Campo para deshabilitar acciones cuando el journey no está activo
        canEdit: this.isJourneyActive(journey)
      };
    });
  }

  /**
   * 🔥 Obtiene el color según el estado del journey
   */
  private getStatusColor(status?: string): ColorType {
    if (!status) {
      return 'white';
    }

    if (['CLOSED', 'CANCELED_BY_USER', 'CANCELED_BY_DRIVER', 'CANCELED_BY_MANAGER', 'CANCEL', 'ERROR', 'EXPIRED'].includes(status)) {
      return 'red';
    }

    if (['TRIP_COMPLETED', 'DRIVER_CONFIRMED'].includes(status)) {
      return 'green';
    }

    if (['RECURRENT_RESERVE_NEW', 'RESERVE_WAITING', 'TRIP_STARTED'].includes(status)) {
      return 'blue';
    }

    return 'white';
  }

  async handleAction(ev: any) {
    console.log('handleAction called in modal', ev);
    if (this.onAction) {
      await this.onAction(ev);
    }
    // Cerrar el modal después de la acción
    if (ev.action === 'view') {
      await this.close();
    }
  }

  async close() {
    await this.modalController.dismiss();
  }
}

